import React from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";

import { AlphabetIndexEntry } from "../history-filters";

type Props = {
  items: AlphabetIndexEntry[];
  activeLetter: string | null;
  onSelect: (item: AlphabetIndexEntry) => void;
};

const AlphabetIndex: React.FC<Props> = ({ items, activeLetter, onSelect }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        order: { xs: 0, lg: 1 },
        position: { lg: "sticky" },
        top: { lg: 96 },
        "--alpha-slot": "clamp(16.8px, calc(((100vh - 220px) / 26) * 0.88), 26.2px)",
        "--alpha-font": "clamp(9.6px, calc(((100vh - 220px) / 34) * 0.88), 11.4px)",
        "--alpha-gap": "clamp(1.2px, calc(((100vh - 220px) / 220) * 0.88), 5px)",
        display: "grid",
        gridTemplateColumns: { xs: "repeat(7, minmax(0, 1fr))", sm: "repeat(9, minmax(0, 1fr))", lg: "1fr" },
        gap: { xs: 0.75, lg: "var(--alpha-gap)" },
        alignContent: "start",
        p: { xs: 1.25, lg: 0.75 },
        borderRadius: "20px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 10px 30px rgba(18, 38, 63, 0.06)",
      }}
    >
      <Typography
        sx={{
          gridColumn: "1 / -1",
          px: 0.5,
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        Índice
      </Typography>

      {items.map((item) => {
        const selected = item.letter === activeLetter;

        return (
          <ButtonBase
            key={item.letter}
            disabled={!item.available}
            aria-label={item.available ? `Ir para pacientes com ${item.letter}` : `Sem pacientes com ${item.letter}`}
            onClick={() => onSelect(item)}
            sx={{
              width: "100%",
              minHeight: { xs: 34, lg: "var(--alpha-slot)" },
              borderRadius: { xs: "12px", lg: "clamp(8px, calc(var(--alpha-slot) / 3), 12px)" },
              border: `1px solid ${selected ? theme.palette.primary.main : item.available ? theme.palette.divider : theme.palette.action.disabledBackground}`,
              borderStyle: item.available ? "solid" : "dashed",
              backgroundColor: selected
                ? theme.palette.primary.main
                : item.available
                  ? theme.palette.background.default
                  : theme.palette.action.disabledBackground,
              color: item.available
                ? selected
                  ? theme.palette.primary.contrastText
                  : theme.palette.primary.main
                : theme.palette.text.disabled,
              fontSize: { xs: "12px", lg: "var(--alpha-font)" },
              fontWeight: 700,
              transition: "all .15s ease",
              "&.Mui-disabled": {
                opacity: 1,
              },
              "&:not(.Mui-disabled):hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: selected ? theme.palette.primary.main : theme.palette.brand.surface2,
              },
            }}
          >
            {item.letter}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default AlphabetIndex;